import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {PAN, ZOOMIN, ZOOMOUT} from 'rc-canvas/lib/ZoomPanModes';
import {BRUSH, FREEHAND, INVERSE, LASSO, RECT} from 'rc-canvas/lib/DrawingModes';
import {ZoomPanContainer} from 'rc-canvas/lib/ZoomPanContainer';
import {PaintCanvas} from 'rc-canvas/lib/PaintCanvas';
import {PaintActionContainer} from 'rc-canvas/lib/PaintActionContainer';
import {ImageDataCanvas} from 'rc-canvas/lib/ImageDataCanvas';
import {Transformation} from 'rc-canvas/lib/Transformation';
import {extractImageData, printImageData, resizeImageData, saveImageDataAs} from 'rc-canvas/lib/Utils';
import {addNotification} from '../notifications';
import {applyEnhancement} from '../actions/EditorCanvasActions';
import isNil from 'lodash/isNil';
import {fileNameAndType} from '../utils';
import {Defaults, ENHANCEMENT, GREY_COLOR, ORIGINAL, POPPED, RGB_GREY_VAL, RGB_MAX_VAL, TRANSPARENT_GREY_COLOR} from '../Constants';
import classNames from 'classnames';

export default class EditorCanvas extends Component {
    static propTypes = {
        srcPrefix: PropTypes.string,
        imageInfo: PropTypes.object.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        activeImage: PropTypes.oneOf([ORIGINAL, POPPED, ENHANCEMENT]),
        brushSize: PropTypes.number,
        tool: PropTypes.oneOf([BRUSH, LASSO, INVERSE, PAN, ZOOMIN, ZOOMOUT, null]),
        shape: PropTypes.oneOf([FREEHAND, RECT, null]),
        eraseBoostLevel: PropTypes.number,
        spread: PropTypes.number,
        amplitude: PropTypes.number,
        highlight: PropTypes.object,
        onEdit: PropTypes.func,
        origImageData: PropTypes.instanceOf(ImageData),
        poppedImageData: PropTypes.instanceOf(ImageData),
        enhImageData: PropTypes.instanceOf(ImageData),
        focusOnMount: PropTypes.bool,
        edited: PropTypes.bool,
        transformation: PropTypes.instanceOf(Transformation),
        onTransformationChange: PropTypes.func,
        onToggleOriginal: PropTypes.func,
    };

    static defaultProps = {
        activeImage: POPPED,
        brushSize: 4,
        tool: BRUSH,
        eraseBoostLevel: -100,
        spread: Defaults.SPATIAL_SCALE,
        amplitude: Defaults.AMPLITUDE,
        focusOnMount: true,
        screenOffsetRight: 0,
        wrapperProps: {},
    };

    componentDidMount() {
        if (this.props.focusOnMount) {
            this.focus();
        }
    }

    componentDidUpdate() {
        if (this.props.focusOnMount) {
            this.focus();
        }
    }

    saveAs = (outFileName, outType) => {
        const {activeImage, imageInfo, amplitude, spread, edited} = this.props;
        const {fullWidth, fullHeight} = imageInfo;
        const {fileName, mimeType} = fileNameAndType({activeImage, imageInfo, amplitude, spread, edited}, outFileName, outType);
        const {poppedImageData, origImageData, enhImageData} = this.props;
        switch (activeImage) {
            case POPPED:
                if (fullWidth) {
                    this.fetchFullOrigImage((fullOrigImageData) => {
                        const fullEnhImageData = resizeImageData(enhImageData, fullWidth, fullHeight);
                        const fullPoppedImageData = applyEnhancement(fullOrigImageData, fullEnhImageData);
                        saveImageDataAs(fullPoppedImageData, fileName, mimeType);
                    });
                } else {
                    saveImageDataAs(poppedImageData, fileName, mimeType);
                }
                break;
            case ORIGINAL:
                if (fullWidth) {
                    this.fetchFullOrigImage((fullOrigImageData) => {
                        saveImageDataAs(fullOrigImageData, fileName, mimeType);
                    });
                } else {
                    saveImageDataAs(origImageData, fileName, mimeType);
                }
                break;
            case ENHANCEMENT:
                if (fullWidth) {
                    const fullEnhImageData = resizeImageData(enhImageData, fullWidth, fullHeight);
                    saveImageDataAs(fullEnhImageData, fileName, mimeType);
                } else {
                    saveImageDataAs(enhImageData, fileName, mimeType);
                }
                break;
            default:
                break;
        }
    };

    fetchFullOrigImage = (onFetchSuccess) => {
        const {imageInfo, srcPrefix} = this.props;
        const {fullSrc, fullWidth, fullHeight} = imageInfo;
        const fullImageSrc = srcPrefix + fullSrc;
        extractImageData(fullImageSrc, fullWidth, fullHeight, 1, onFetchSuccess, (url) => {
            addNotification({message: 'Failed to load image ' + url, level: 'error', position: 'bl'});
        })
    };

    focus = () => {
        if (this.zoom) {
            this.zoom.focus();
        }
    };

    resetView = (e) => {
        if (this.zoom) {
            this.zoom.reset(e);
        }
    };

    print = () => {
        const {activeImage, poppedImageData, origImageData, enhImageData} = this.props;
        switch (activeImage) {
            case ORIGINAL:
                printImageData(origImageData);
                break;
            case ENHANCEMENT:
                printImageData(enhImageData);
                break;
            case POPPED:
            default:
                printImageData(poppedImageData);
                break;
        }
    };

    combineTransformation = (transformation, highlight) => {
        if (!highlight || !highlight.historyProps) {
            return transformation;
        }
        const highlightTransformation = highlight.historyProps.transformation;
        if (!highlightTransformation) {
            return transformation;
        }

        return highlightTransformation.inverse().combine(transformation);
    };

    render() {
        const {origImageData, poppedImageData, enhImageData} = this.props;
        if (!origImageData || !poppedImageData || !enhImageData) {
            return false;
        }
        const {
            width, height, imageInfo, tool, shape, brushSize, eraseBoostLevel, activeImage, onEdit, highlight,
            focusOnMount, onTransformationChange, onToggleOriginal
        } = this.props;
        const paint = tool === BRUSH || tool === LASSO || tool === INVERSE;
        const imageWidth = imageInfo.width, imageHeight = imageInfo.height;
        const scale = Math.min(width / imageWidth, height / imageHeight, 1);
        const initialTransformation = new Transformation(
            scale,
            Math.max(0, width - imageWidth * scale) / 2,
            Math.max(0, height - imageHeight * scale) / 2
        );

        let brushColor;
        if (eraseBoostLevel === -1) {
            brushColor = activeImage === ENHANCEMENT ? GREY_COLOR : TRANSPARENT_GREY_COLOR;
        } else if (eraseBoostLevel < 0) {
            const g = RGB_MAX_VAL - Math.floor(-eraseBoostLevel / 100 * RGB_GREY_VAL);
            brushColor = activeImage === ENHANCEMENT ? 'rgb(' + g + ', ' + g + ', 255)' : 'rgba(' + g + ', ' + g + ', 255, 0.01)';
        } else if (eraseBoostLevel === 0) {
            brushColor = 'rgba(255, 255, 255, 0)';
        } else {
            const g = RGB_MAX_VAL - Math.floor(eraseBoostLevel / 100 * RGB_GREY_VAL);
            brushColor = activeImage === ENHANCEMENT ? 'rgb(255, ' + g + ', ' + g + ')' : 'rgba(255, ' + g + ', ' + g + ', 0.01)';
        }
        const zIndex = paint ? 1 : 10;
        const transformation = initialTransformation.combine(this.props.transformation);

        return (
            <div style={{width, height, position: 'relative'}} onClick={isNil(tool) ? onToggleOriginal : null}>
                <ZoomPanContainer
                    width={width}
                    height={height}
                    mode={tool} ref={c => this.zoom = c}
                    allowFocus={focusOnMount}
                    transformation={this.props.transformation}
                    onTransformationChange={onTransformationChange}
                    className={classNames('zoom-pan-container', {[`brush-${brushSize}`]: tool === BRUSH})}
                >
                    <div style={{zIndex, position: 'absolute', top: 0, left: 0, width, height}}>
                        <div style={{display: activeImage === ORIGINAL ? 'block' : 'none', width, height}}>
                            <ImageDataCanvas imageData={origImageData} transformation={transformation} width={width} height={height}/>
                        </div>
                        <div style={{display: activeImage === POPPED ? 'block' : 'none', width, height}}>
                            <ImageDataCanvas
                                imageData={poppedImageData} transformation={transformation} width={width} height={height}
                                ref={c => this.poppedCanvas = c}/>
                        </div>
                        <div style={{display: activeImage === ENHANCEMENT ? 'block' : 'none', width, height}}>
                            <ImageDataCanvas imageData={enhImageData} transformation={transformation} width={width} height={height}/>
                        </div>
                    </div>
                    <div style={{zIndex: 50, position: 'absolute', top: 0, left: 0, width, height}}>
                        <PaintCanvas
                            drawing={false} actions={highlight} width={width} height={height}
                            transformation={this.combineTransformation(transformation, highlight)}/>
                    </div>
                    <div style={{zIndex: paint ? 100 : 1, position: 'absolute', top: 0, left: 0, width, height}}>
                        <PaintActionContainer
                            width={width} height={height} mode={tool} ref={c => this.hist = c}
                            brushSize={brushSize}
                            brushColor={brushColor}
                            shape={shape}
                            historyProps={{transformation, eraseBoostLevel, width, height}}
                            onMouseUp={onEdit}
                        >{(drawing, lastAction) => (
                            <PaintCanvas drawing={drawing} actions={lastAction} width={width} height={height}/>
                        )}</PaintActionContainer>
                    </div>
                </ZoomPanContainer>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/EditorCanvas.js